/**
 * Consecutive SSE-drop tracker shared by the dashboard's two SSE consumers
 * (comments CommentThread.tsx, notifications useNotifications.ts).
 *
 * A single drop is routine: the upstream edge enforces a ~15-minute
 * max-connection-duration on every HTTP request, so healthy streams are
 * recycled on a cadence and the client reconnect self-heals (observed
 * 2026-07-03; see debug_log). Logging each drop at error would mint a false
 * PostHog $exception every cycle. But a stream that repeatedly fails to reach
 * a subscribed state (e.g. the server's TOO_MANY_CONNECTIONS path, which 200s,
 * emits an `error` event, and closes without the success event) is a genuine
 * failure worth surfacing.
 *
 * Contract: recordDrop() returns the severity to log at — 'warn' below the
 * threshold, 'error' at and beyond it. recordConnected() must be called on the
 * server's SUCCESS event (comments: `connected`, notifications: `heartbeat`),
 * NOT EventSource.onopen — onopen fires even when the server accepts the
 * request and then rejects in-stream.
 */
export const SSE_DROP_ESCALATION_THRESHOLD = 3;

export interface SseDropTracker {
  /** Record a drop; returns the log severity for it. */
  recordDrop: () => 'warn' | 'error';
  /** Record a successful subscription; resets the streak. */
  recordConnected: () => void;
  /** Current consecutive drop count (for log context). */
  consecutiveDrops: () => number;
}

export function createSseDropTracker(): SseDropTracker {
  let drops = 0;
  return {
    recordDrop() {
      drops += 1;
      return drops >= SSE_DROP_ESCALATION_THRESHOLD ? 'error' : 'warn';
    },
    recordConnected() {
      drops = 0;
    },
    consecutiveDrops() {
      return drops;
    },
  };
}
